import { useState, useRef, useEffect } from "react";

const C = {
  white:"#FFFFFF",offWhite:"#F8FAFC",bg:"#F1F5F9",ink:"#0A0E1A",inkMid:"#1E293B",
  body:"#475569",muted:"#94A3B8",ghost:"#CBD5E1",blue:"#2563EB",blueLt:"#DBEAFE",
  blueMid:"#93C5FD",silver:"#64748B",silverLt:"#E2E8F0",red:"#EF4444",redLt:"#FEF2F2",
  redBorder:"#FECACA",amber:"#D97706",amberLt:"#FFFBEB",amberBorder:"#FDE68A",
  green:"#059669",greenLt:"#ECFDF5",greenBorder:"#A7F3D0",gold:"#F59E0B",
};

const STRIPE_PUBLISHABLE_KEY = "pk_live_IjwC32ShNPA0f8EBJ0BEqFyi00kwEtd7do";
const AUDIT_PRICE_CENTS = 4900;
const FILM_ACCESS_KEY = "hunndred2026";

const SYSTEM_PROMPT = `You are a ruthlessly thorough local SEO and online presence auditor. Your job is to find every place a business appears online and identify every inconsistency, unclaimed listing, and missed opportunity.

You will be given: a legal business name, a brand/customer-facing name, website, city/state, phone, and business type.

Search the web extensively using BOTH the legal name AND the brand name to find every listing. Also search industry-specific directories based on the business type:
- Restaurants: Google, Facebook, Instagram, Yelp, TripAdvisor, OpenTable, DoorDash, Grubhub, Apple Maps, Yellow Pages, BBB
- Law Firms: Google, Facebook, Avvo, Justia, FindLaw, Martindale, LinkedIn, Apple Maps, Yellow Pages, BBB
- Insurance: Google, Facebook, Yelp, Insurance.com, Yellow Pages, Chamber, BBB, Apple Maps
- Salons/Spas: Google, Facebook, Instagram, Yelp, StyleSeat, Vagaro, Apple Maps, Yellow Pages, BBB
- Medical/Dental: Google, Facebook, Healthgrades, Zocdoc, WebMD, Vitals, Apple Maps, Yellow Pages, BBB
- ALL businesses: Google Business Profile, Facebook, Instagram, Yelp, Apple Maps, Yellow Pages, ZoomInfo, LinkedIn, BBB, Chamber of Commerce, local directories

ACCURACY RULES FOR SEARCH:
1. Always search using BOTH the brand name AND the legal name. If a platform is not found under one name, search again with the other name before marking it missing.
2. For Google Business Profile, Facebook, and Instagram specifically: use "site:" searches (site:facebook.com, site:instagram.com) if your initial general search did not find them. These are the three most important platforms and false negatives are unacceptable.
3. Only mark a major platform as "missing" or "not found" if you searched for it with both name variations and still found nothing. If uncertain, set status to "missing" and claimed to "unknown".
4. False negatives (reporting a listing does not exist when it does) are the worst possible error. When in doubt, mark as "unknown" rather than "not found".

Return ONLY valid JSON — no markdown, no explanation, no code fences:

{
  "business_name": "<brand name>",
  "legal_name": "<legal name>",
  "score": <0-100>,
  "grade": "<A|B|C|D|F>",
  "summary": "<2-3 punchy sentences on the biggest problems>",
  "name_variations": ["<brand name first, then every variation found>"],
  "platforms": [
    {
      "platform": "<platform name>",
      "found": <true|false>,
      "current_name": "<exact name it shows, or Not found>",
      "correct_name": "<what it should say>",
      "status": "<critical|warning|good|missing>",
      "claimed": "<yes|no|unknown>",
      "issue": "<specific problem in one sentence>",
      "action": "<exact step to fix it>"
    }
  ],
  "critical_issues": ["<issue 1>","<issue 2>","<issue 3>"],
  "quick_wins": ["<fix under 10 min>","<fix under 10 min>","<fix under 10 min>"],
  "tagline_present": <true|false>,
  "tagline_platforms": ["<platforms where tagline was found>"],
  "nap_consistent": <true|false>,
  "recommended_tagline_placements": ["<platform: exact placement>"],
  "industry_directories_missing": ["<directory name the business should be on but isn't>"]
}

IMPORTANT ACCURACY RULES:
- If you search for a listing and cannot find it, mark claimed as "unknown" NOT "no". Only mark claimed as "no" if you find clear evidence the listing exists but is unclaimed (e.g. Google shows an unclaimed listing prompt, or Yelp shows "Is this your business?").
- If a platform listing simply does not appear in search results, set status to "missing" and claimed to "unknown". Do NOT assume it is unclaimed — the business may have a listing that did not surface in your search.
- Search thoroughly: try multiple query variations including the legal name, brand name, brand name + city, and brand name + business type.
- For Google Business Profile specifically, search "[business name] [city]" and look for the knowledge panel or map pack. If neither appears, try "[business name] Google Business Profile". Only mark as unclaimed if Google explicitly shows an unclaimed listing.

Scoring: start at 100, subtract -15 per confirmed unclaimed critical platform (Google, Facebook, Yelp, Apple Maps — only if you found evidence it is unclaimed, not just missing from search), -10 per name mismatch on major platform, -8 if old/duplicate website still indexing, -5 if no website, -3 per minor directory mismatch, -2 per missing industry-specific directory that is relevant to this business type. Do NOT penalize for directories irrelevant to the business type.
Grade: 90-100=A, 75-89=B, 55-74=C, 35-54=D, 0-34=F`;



const QUESTIONS_EN = [
  { id:"legal_name",    label:"Legal Business Name",           placeholder:"e.g. Smith & Associates LLC",       text:"What's the legal or registered business name? (the official LLC or registered name)" },
  { id:"brand_name",   label:"Brand / Customer-Facing Name",  placeholder:"e.g. Smith Law Group",              text:"What's the brand name customers actually know them by? (may be different from the legal name)" },
  { id:"website",      label:"Website URL",                   placeholder:"e.g. smithlawgroup.com",            text:"What's their website URL? (type 'none' if they don't have one)" },
  { id:"location",     label:"City & State",                  placeholder:"e.g. Chicago, IL",                  text:"What city and state are they in?" },
  { id:"phone",        label:"Phone Number",                  placeholder:"e.g. (312) 555-0100",               text:"What's their primary phone number? (type 'skip' if unknown)" },
  { id:"business_type",label:"Business Type",                 placeholder:"e.g. Restaurant, Salon, Law Firm...",text:"What type of business is it? (this helps us find industry-specific directories)" },
];

const QUESTIONS_ES = [
  { id:"legal_name",    label:"Nombre Legal del Negocio",      placeholder:"ej. Servicios Rangel LLC",          text:"¿Cuál es el nombre legal o registrado del negocio? (el nombre oficial de la LLC o registro)" },
  { id:"brand_name",   label:"Nombre de Marca / Comercial",   placeholder:"ej. Rangel Services",               text:"¿Cuál es el nombre comercial que los clientes conocen? (puede ser diferente al nombre legal)" },
  { id:"website",      label:"Sitio Web",                     placeholder:"ej. rangelservices.com",             text:"¿Cuál es la dirección de su sitio web? (escribe 'ninguno' si no tienen)" },
  { id:"location",     label:"Ciudad y Estado",               placeholder:"ej. Chicago, IL",                   text:"¿En qué ciudad y estado se encuentra el negocio?" },
  { id:"phone",        label:"Número de Teléfono",            placeholder:"ej. (312) 555-0100",                text:"¿Cuál es el número de teléfono principal? (escribe 'omitir' si no lo sabes)" },
  { id:"business_type",label:"Tipo de Negocio",               placeholder:"ej. Restaurante, Salón, Taller...", text:"¿Qué tipo de negocio es? (esto nos ayuda a encontrar directorios específicos de la industria)" },
];

const LOADING_MSGS_EN = [
  "Searching Google Business Profile...",
  "Checking Facebook & Instagram...",
  "Scanning Yelp and Apple Maps...",
  "Searching industry directories...",
  "Cross-referencing legal vs brand name...",
  "Checking for unclaimed listings...",
  "Analyzing NAP consistency...",
  "Compiling your report...",
];

const LOADING_MSGS_ES = [
  "Buscando en Google Business Profile...",
  "Revisando Facebook e Instagram...",
  "Escaneando Yelp y Apple Maps...",
  "Buscando directorios de la industria...",
  "Comparando nombre legal vs comercial...",
  "Verificando listados sin reclamar...",
  "Analizando consistencia NAP...",
  "Preparando tu reporte...",
];

const T = {
  en: {
    nav_price: "Full Report — $49",
    nav_cta: "Start Free Audit →",
    hero_badge: "Free to scan · Full report unlocks for $49",
    hero_h1_1: "Find Every Place Your",
    hero_h1_2: "Business Is Invisible.",
    hero_sub: "We scan Google, Yelp, Facebook, Apple Maps, and 12+ more platforms — then show you every name mismatch, unclaimed listing, and missed opportunity in one clean report.",
    hero_cta: "Run My Free Audit →",
    hero_note: "Scan is free. Full report with every fix unlocks for $49.",
    how_label: "How It Works",
    steps: [
      { num:"01", title:"Answer 6 Questions",  body:"Tell us the business name, location, type, and phone. Takes under 2 minutes." },
      { num:"02", title:"We Scan Everything",   body:"Our AI searches 12+ platforms and industry-specific directories for every listing." },
      { num:"03", title:"Get Your Full Report", body:"See your grade, every issue found, and the exact steps to fix each one — for $49." },
    ],
    pricing_label: "Full Audit Report",
    pricing_sub: "Complete audit with every fix, ranked by impact.",
    pricing_onetime: "one-time",
    features: [
      "12+ platforms scanned including industry directories",
      "Every name mismatch identified with exact fix steps",
      "NAP consistency check across all listings",
      "Unclaimed listings flagged with claim instructions",
      "Critical issues ranked by SEO impact",
      "Quick wins — fixes under 10 minutes",
      "Downloadable PDF report",
      "Industry-specific directory audit",
    ],
    pricing_cta: "Start Free Audit →",
    pricing_note: "Scan is always free. Pay $49 to unlock your full report.",
    platforms_label: "Platforms We Scan",
    footer_tagline: "AI-powered business presence scanner",
    loading_title: "Scanning",
    loading_slow: "This is taking a bit longer than usual — hang tight, we're being thorough...",
    chat_step: "Step",
    chat_of: "/",
    chat_back: "← Back",
    report_new: "← New Audit",
    report_save: "⬇ Save PDF",
    report_legal: "Legal:",
    report_confirm_reset: "Start a new audit? Your current report will be cleared.",
    nap_title: "NAP INCONSISTENCY DETECTED",
    nap_body: "Name, address, and/or phone do not match across platforms — this directly hurts local search rankings.",
    name_var_label: "NAME VARIATIONS FOUND ONLINE",
    platform_label: "PLATFORM FINDINGS",
    unlock_title_suffix: "More Platforms Found",
    unlock_ready: "Full Analysis Ready",
    unlock_sub: "Unlock the complete report — every platform, every fix, critical issues ranked by impact, quick wins, and your downloadable PDF.",
    unlock_features: [
      "All platforms fully detailed",
      "Every fix step written out",
      "Critical issues ranked by impact",
      "Quick wins under 10 minutes",
      "Name variation full breakdown",
      "Industry directories missing",
      "NAP consistency analysis",
      "Downloadable PDF report",
    ],
    unlock_cta: "Unlock Full Report — $49",
    unlock_processing: "Processing...",
    unlock_secure: "Secure payment · One-time · Instant access",
    critical_title: "🚨 CRITICAL ISSUES",
    quickwins_title: "⚡ QUICK WINS",
    industry_missing: "INDUSTRY DIRECTORIES — NOT LISTED",
    tagline_found: "✓ FOUND ON SOME PLATFORMS",
    tagline_missing: "✗ NOT DEPLOYED — ADD IT TO:",
    footer_cta_title: "Ready to fix all of this?",
    footer_cta_sub: "Download your full report and work through every fix.",
    footer_pdf: "⬇ Download PDF",
    footer_another: "Run Another Audit",
    error_title: "Audit Failed",
    error_cta: "← Try Again",
    claimed_yes: "✓ Claimed",
    claimed_no: "✗ Unclaimed",
    should_be: "→ should be",
    score_label: "PRESENCE SCORE",
    print_label: "Online Presence Audit",
    print_sub: "A complete audit of your online presence — every listing, every name mismatch, and every fix ranked by impact.",
    print_fields: ["Business Type","Location","Website","Phone","Score","Grade"],
    print_finding: "Key Finding",
    print_labels: ["Critical","Warnings","Good","Scanned"],
    grade_labels: { A:"Excellent", B:"Good", C:"Fair", D:"Poor", F:"Critical" },
  },
  es: {
    nav_price: "Reporte Completo — $49",
    nav_cta: "Iniciar Auditoría Gratis →",
    hero_badge: "Escaneo gratis · Reporte completo se desbloquea por $49",
    hero_h1_1: "Encuentra Cada Lugar Donde Tu",
    hero_h1_2: "Negocio Es Invisible.",
    hero_sub: "Escaneamos Google, Yelp, Facebook, Apple Maps y más de 12 plataformas — luego te mostramos cada inconsistencia de nombre, listado sin reclamar y oportunidad perdida en un reporte claro.",
    hero_cta: "Realizar Mi Auditoría Gratis →",
    hero_note: "El escaneo es gratis. El reporte completo con cada corrección se desbloquea por $49.",
    how_label: "Cómo Funciona",
    steps: [
      { num:"01", title:"Responde 6 Preguntas",     body:"Dinos el nombre del negocio, ubicación, tipo y teléfono. Toma menos de 2 minutos." },
      { num:"02", title:"Escaneamos Todo",           body:"Nuestra IA busca en más de 12 plataformas y directorios específicos de la industria." },
      { num:"03", title:"Obtén Tu Reporte Completo", body:"Ve tu calificación, cada problema encontrado y los pasos exactos para resolverlos — por $49." },
    ],
    pricing_label: "Reporte Completo de Auditoría",
    pricing_sub: "Auditoría completa con cada corrección, clasificada por impacto.",
    pricing_onetime: "pago único",
    features: [
      "Más de 12 plataformas escaneadas incluyendo directorios de industria",
      "Cada inconsistencia de nombre identificada con pasos exactos",
      "Verificación de consistencia NAP en todos los listados",
      "Listados sin reclamar marcados con instrucciones",
      "Problemas críticos clasificados por impacto SEO",
      "Victorias rápidas — correcciones en menos de 10 minutos",
      "Reporte PDF descargable",
      "Auditoría de directorios específicos de la industria",
    ],
    pricing_cta: "Iniciar Auditoría Gratis →",
    pricing_note: "El escaneo siempre es gratis. Paga $49 para desbloquear tu reporte completo.",
    platforms_label: "Plataformas que Escaneamos",
    footer_tagline: "Escáner de presencia digital impulsado por IA",
    loading_title: "Escaneando",
    loading_slow: "Esto está tomando un poco más de lo usual — quédate, estamos siendo minuciosos...",
    chat_step: "Paso",
    chat_of: "/",
    chat_back: "← Atrás",
    report_new: "← Nueva Auditoría",
    report_save: "⬇ Guardar PDF",
    report_legal: "Legal:",
    report_confirm_reset: "¿Iniciar una nueva auditoría? Tu reporte actual se borrará.",
    nap_title: "INCONSISTENCIA NAP DETECTADA",
    nap_body: "El nombre, dirección y/o teléfono no coinciden entre plataformas — esto afecta directamente el posicionamiento en búsquedas locales.",
    name_var_label: "VARIACIONES DE NOMBRE ENCONTRADAS EN LÍNEA",
    platform_label: "RESULTADOS POR PLATAFORMA",
    unlock_title_suffix: "Plataformas Más Encontradas",
    unlock_ready: "Análisis Completo Listo",
    unlock_sub: "Desbloquea el reporte completo — cada plataforma, cada corrección, problemas críticos clasificados por impacto, victorias rápidas y tu PDF descargable.",
    unlock_features: [
      "Todas las plataformas detalladas",
      "Cada paso de corrección escrito",
      "Problemas críticos por impacto",
      "Victorias rápidas en 10 minutos",
      "Desglose completo de variaciones",
      "Directorios de industria faltantes",
      "Análisis de consistencia NAP",
      "Reporte PDF descargable",
    ],
    unlock_cta: "Desbloquear Reporte Completo — $49",
    unlock_processing: "Procesando...",
    unlock_secure: "Pago seguro · Único · Acceso inmediato",
    critical_title: "🚨 PROBLEMAS CRÍTICOS",
    quickwins_title: "⚡ VICTORIAS RÁPIDAS",
    industry_missing: "DIRECTORIOS DE INDUSTRIA — NO LISTADO",
    tagline_found: "✓ ENCONTRADO EN ALGUNAS PLATAFORMAS",
    tagline_missing: "✗ NO IMPLEMENTADO — AGREGAR A:",
    footer_cta_title: "¿Listo para resolver todo esto?",
    footer_cta_sub: "Descarga tu reporte completo y trabaja en cada corrección.",
    footer_pdf: "⬇ Descargar PDF",
    footer_another: "Realizar Otra Auditoría",
    error_title: "Auditoría Fallida",
    error_cta: "← Intentar de Nuevo",
    claimed_yes: "✓ Reclamado",
    claimed_no: "✗ Sin Reclamar",
    should_be: "→ debería ser",
    score_label: "PUNTUACIÓN DE PRESENCIA",
    print_label: "Auditoría de Presencia en Línea",
    print_sub: "Una auditoría completa de tu presencia en línea — cada listado, cada inconsistencia de nombre y cada corrección clasificada por impacto.",
    print_fields: ["Tipo de Negocio","Ubicación","Sitio Web","Teléfono","Puntuación","Calificación"],
    print_finding: "Hallazgo Principal",
    print_labels: ["Críticos","Advertencias","Buenos","Escaneados"],
    grade_labels: { A:"Excelente", B:"Bueno", C:"Regular", D:"Deficiente", F:"Crítico" },
  },
};

const gradeColor = g => ({ A:C.green, B:C.blue, C:C.amber, D:"#EA580C", F:C.red }[g] || C.muted);
const statusCfg  = s => ({
  critical:{ dot:C.red,   bg:C.redLt,   border:C.redBorder,   badge:"CRITICAL",  badgeBg:C.red,   badgeText:C.white },
  warning: { dot:C.amber, bg:C.amberLt, border:C.amberBorder, badge:"NEEDS FIX", badgeBg:C.amber, badgeText:C.white },
  good:    { dot:C.green, bg:C.greenLt, border:C.greenBorder, badge:"GOOD",      badgeBg:C.green, badgeText:C.white },
  missing: { dot:C.muted, bg:C.offWhite,border:C.ghost,       badge:"MISSING",   badgeBg:C.muted, badgeText:C.white },
}[s] || { dot:C.muted, bg:C.offWhite, border:C.ghost, badge:s, badgeBg:C.muted, badgeText:C.white });

export default function AuditBot() {
  const [lang, setLang]             = useState("en");
  const [phase, setPhase]           = useState("landing");
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState("");
  const [qIndex, setQIndex]         = useState(0);
  const [answers, setAnswers]       = useState({});
  const [auditData, setAuditData]   = useState(null);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [errorMsg, setErrorMsg]     = useState("");
  const [loadPct, setLoadPct]       = useState(0);
  const [unlocked, setUnlocked]     = useState(false);
  const [paying, setPaying]         = useState(false);
  const [payError, setPayError]     = useState("");
  const [slowWarning, setSlowWarning] = useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const t  = T[lang];
  const Qs = lang === "es" ? QUESTIONS_ES : QUESTIONS_EN;
  const LOADING_MSGS = lang === "es" ? LOADING_MSGS_ES : LOADING_MSGS_EN;

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);
  useEffect(() => { if (phase==="chat") setTimeout(()=>inputRef.current?.focus(), 80); }, [messages, phase]);

  // FIX #8: Loading phase with timeout warning
  useEffect(() => {
    if (phase!=="loading") { setSlowWarning(false); return; }
    let i=0, pct=0;
    const loadMsgs = lang === "es" ? LOADING_MSGS_ES : LOADING_MSGS_EN;
    const iv = setInterval(() => {
      i=(i+1)%loadMsgs.length;
      pct=Math.min(pct+Math.random()*13+5, 92);
      setLoadingMsg(loadMsgs[i]);
      setLoadPct(Math.round(pct));
    }, 2500);
    // FIX #12: Slow warning after 60s
    const slowTimer = setTimeout(() => setSlowWarning(true), 60000);
    return () => { clearInterval(iv); clearTimeout(slowTimer); };
  }, [phase, lang]);

  // FIX #1 & #11: Secure payment verification via Stripe session_id + film access bypass
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Film access bypass for case study filming
    if (params.get("filmaccess") === FILM_ACCESS_KEY) {
      setUnlocked(true);
    }

    // Stripe payment verification
    const sessionId = params.get("session_id");
    if (sessionId) {
      const saved = localStorage.getItem("pa_audit");
      const savedAnswers = localStorage.getItem("pa_answers");

      // Verify payment with Stripe
      fetch("/api/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.verified) {
            setUnlocked(true);
            if (saved) {
              try {
                setAuditData(JSON.parse(saved));
                if (savedAnswers) setAnswers(JSON.parse(savedAnswers));
                setPhase("report");
              } catch(e) {}
            }
          }
          // Clean up localStorage and URL
          localStorage.removeItem("pa_audit");
          localStorage.removeItem("pa_answers");
          window.history.replaceState({}, "", window.location.pathname);
        })
        .catch(() => {
          localStorage.removeItem("pa_audit");
          localStorage.removeItem("pa_answers");
        });
    }
  }, []);

  // FIX #7: Reset answers too when switching language mid-chat
  const switchLang = (l) => {
    setLang(l);
    if (phase === "landing") return;
    setMessages([{ role:"bot", text: (l==="es" ? QUESTIONS_ES : QUESTIONS_EN)[0].text }]);
    setQIndex(0);
    setInput("");
    setAnswers({});
  };

  const startAudit = () => {
    setMessages([{role:"bot", text:Qs[0].text}]);
    setPhase("chat");
  };

  // FIX #6: Go back to previous question
  const goBack = () => {
    if (qIndex === 0) return;
    const prevQ = qIndex - 1;
    // Remove last bot message and last user message
    const newMsgs = messages.slice(0, -2);
    // Remove the answer for the current question's previous
    const newAnswers = { ...answers };
    delete newAnswers[Qs[prevQ].id];
    setAnswers(newAnswers);
    setMessages(newMsgs);
    setQIndex(prevQ);
    setInput(answers[Qs[prevQ].id] || "");
  };

  const submitAnswer = async () => {
    if (!input.trim()) return;
    const val = input.trim(); setInput("");
    const newMsgs    = [...messages, {role:"user", text:val}];
    const newAnswers = {...answers, [Qs[qIndex].id]: val};
    setAnswers(newAnswers);
    const next = qIndex + 1;
    if (next < Qs.length) {
      setMessages([...newMsgs, {role:"bot", text:Qs[next].text}]);
      setQIndex(next);
    } else {
      setMessages([...newMsgs, {
        role:"bot",
        text: lang==="es"
          ? `Perfecto. Realizando una auditoría completa para ${newAnswers.brand_name || newAnswers.legal_name} ahora.\n\nBuscando en Google, Yelp, Facebook, Apple Maps, directorios de industria y más. Dame unos 30 segundos...`
          : `Perfect. Running a full audit for ${newAnswers.brand_name || newAnswers.legal_name} now.\n\nSearching Google, Yelp, Facebook, Apple Maps, industry directories, and more. Give me about 30 seconds...`
      }]);
      setPhase("loading"); setLoadPct(0);
      await runAudit(newAnswers);
    }
  };

  const runAudit = async (info) => {
    try {
      const prompt = `Audit the online presence for this business:

Legal/Registered Name: ${info.legal_name}
Brand Name (customer-facing): ${info.brand_name}
Website: ${info.website || "unknown"}
Location: ${info.location || "unknown"}
Phone: ${info.phone || "unknown"}
Business Type: ${info.business_type || "unknown"}

Search using BOTH the legal name and brand name. Find every listing across all relevant platforms and industry-specific directories. Return only the JSON.`;

      // FIX #3: Only send messages — model, system, tools are locked server-side
      let msgs=[{role:"user",content:prompt}], finalText="";
      for (let iter=0; iter<12; iter++) {
        let data, res;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            res = await fetch("/api/audit",{
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                model:"claude-haiku-4-5-20251001", max_tokens:5000,
                system:SYSTEM_PROMPT,
                tools:[{type:"web_search_20250305", name:"web_search"}],
                messages:msgs,
              })
            });
            const text = await res.text();
            data = JSON.parse(text);
            if (!res.ok) throw new Error(data.error?.message || "API error " + res.status);
            break;
          } catch (e) {
            if (attempt === 2) throw e;
            await new Promise(r => setTimeout(r, 2000));
          }
        }
        const texts = (data.content||[]).filter(b=>b.type==="text");
        if (texts.length) finalText = texts.map(b=>b.text).join("");
        if (data.stop_reason==="end_turn") break;
        if (data.stop_reason==="tool_use") {
          msgs.push({role:"assistant", content:data.content});
          const results = (data.content||[]).filter(b=>b.type==="tool_use")
            .map(b=>({type:"tool_result", tool_use_id:b.id, content:"Results retrieved."}));
          if (results.length) msgs.push({role:"user", content:results});
        }
      }
      const match = finalText.match(/\{[\s\S]*\}/);
      if (!match) throw new Error(lang==="es" ? "No se pudo analizar el resultado. Por favor intenta de nuevo." : "Could not parse audit results. Please try again.");
      setAuditData(JSON.parse(match[0]));
      setLoadPct(100);
      setTimeout(()=>setPhase("report"), 500);
    } catch(err) {
      setErrorMsg(err.message);
      setPhase("error");
    }
  };

  // FIX #5: Save audit data before redirect, verify on return
  const handlePayment = async () => {
    setPaying(true); setPayError("");
    try {
      if (auditData) localStorage.setItem("pa_audit", JSON.stringify(auditData));
      if (Object.keys(answers).length) localStorage.setItem("pa_answers", JSON.stringify(answers));
      const res = await fetch("/api/checkout", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
      throw new Error(data.error || "Checkout failed");
    } catch(err) {
      setPayError(err.message);
      setPaying(false);
    }
  };

  // FIX #9: Confirmation before reset
  const reset = () => {
    if (auditData) {
      if (!window.confirm(t.report_confirm_reset)) return;
    }
    setPhase("landing"); setMessages([]); setQIndex(0);
    setAnswers({}); setAuditData(null); setInput("");
    setErrorMsg(""); setLoadPct(0); setUnlocked(false);
    setPaying(false); setPayError(""); setSlowWarning(false);
  };

  // FIX #2: Mobile-responsive CSS with media queries
  const GS = `
    *{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;background:${C.bg};}
    @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
    @keyframes slideIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes shimmer{0%{opacity:.5}50%{opacity:1}100%{opacity:.5}}
    .fu{animation:fadeUp .45s ease forwards;}
    .fu1{animation-delay:.05s;opacity:0;} .fu2{animation-delay:.1s;opacity:0;}
    .fu3{animation-delay:.15s;opacity:0;} .fu4{animation-delay:.2s;opacity:0;}
    .si{animation:slideIn .2s ease;}
    .hov{transition:all .15s ease;cursor:pointer;} .hov:hover{opacity:.85;}
    .lock-blur{filter:blur(4px);pointer-events:none;user-select:none;}
    @media(max-width:640px){
      .desktop-logo{display:none!important;}
      .mobile-logo{display:block!important;}
      .nav-price{display:none!important;}
      .steps-grid{grid-template-columns:1fr!important;}
      .stats-row{flex-direction:column!important;align-items:stretch!important;}
      .stats-row>div{flex:1!important;min-width:auto!important;}
      .report-meta{flex-direction:column!important;align-items:flex-start!important;}
      .report-stats{width:100%!important;justify-content:space-between!important;}
      .unlock-features{grid-template-columns:1fr!important;}
      .issues-grid{grid-template-columns:1fr!important;}
      .report-nav-btns{gap:4px!important;}
      .report-nav-btns button{padding:6px 10px!important;font-size:11px!important;}
      .score-card-top{flex-direction:column!important;}
      .chat-bubble{max-width:85%!important;}
      .landing-nav-actions{gap:6px!important;}
      .landing-nav-actions .nav-cta-btn{padding:8px 14px!important;font-size:12px!important;}
      .hero-heading{font-size:32px!important;letter-spacing:-1px!important;}
      .hero-sub{font-size:15px!important;}
      .pricing-section{margin:40px auto 0!important;padding:0 16px!important;}
    }
    @media print{.np{display:none!important;}.po{display:flex!important;}.pb{page-break-after:always;}body,#root{background:white;}}
    .po{display:none;}
  `;

  // ── LANG TOGGLE ─────────────────────────────────────────────────────────────
  const LangToggle = () => (
    <div style={{display:"flex", gap:4, alignItems:"center"}}>
      {["en","es"].map(l=>(
        <button key={l} onClick={()=>switchLang(l)}
          style={{background:lang===l?C.ink:C.offWhite, color:lang===l?C.white:C.silver, border:`1px solid ${lang===l?C.ink:C.silverLt}`, borderRadius:6, padding:"5px 10px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit", transition:"all .15s"}}>
          {l==="en"?"EN":"ES"}
        </button>
      ))}
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // LANDING (FIX #2: mobile responsive)
  // ══════════════════════════════════════════════════════════════════════════
  if (phase==="landing") return (
    <div style={{minHeight:"100vh", background:C.white, fontFamily:"inherit"}}>
      <style>{GS}</style>
      <nav style={{borderBottom:`1px solid ${C.silverLt}`, padding:"0 20px", height:80, display:"flex", alignItems:"center", justifyContent:"space-between", background:C.white, position:"sticky", top:0, zIndex:10}}>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <img src="/Presence_Audit_Logo.png" alt="PresenceAudit" className="desktop-logo" style={{width:120, height:120, objectFit:"contain"}}/>
        </div>
        <div className="landing-nav-actions" style={{display:"flex", alignItems:"center", gap:12}}>
          <LangToggle/>
          <span className="nav-price" style={{fontSize:14, fontWeight:700, color:C.body}}>{t.nav_price}</span>
          <button className="hov nav-cta-btn" onClick={startAudit} style={{background:C.ink, color:C.white, border:"none", borderRadius:8, padding:"9px 20px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit"}}>{t.nav_cta}</button>
        </div>
      </nav>

      <div style={{maxWidth:860, margin:"0 auto", padding:"60px 20px 0", textAlign:"center"}}>
        <div style={{display:"none"}} className="mobile-logo"><img src="/Presence_Audit_Logo.png" alt="PresenceAudit" style={{width:150, height:150, objectFit:"contain", margin:"0 auto 24px"}}/></div>
        <div className="fu" style={{display:"inline-flex", alignItems:"center", gap:7, background:C.offWhite, border:`1px solid ${C.silverLt}`, borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:600, color:C.silver, marginBottom:28}}>
          <span style={{width:7, height:7, borderRadius:"50%", background:C.green, display:"inline-block"}}/>
          {t.hero_badge}
        </div>
        <h1 className="fu fu1 hero-heading" style={{fontSize:"clamp(32px,5.5vw,62px)", fontWeight:900, color:C.ink, lineHeight:1.08, letterSpacing:"-2px", marginBottom:22}}>
          {t.hero_h1_1}<br/><span style={{color:C.blue}}>{t.hero_h1_2}</span>
        </h1>
        <p className="fu fu2 hero-sub" style={{fontSize:18, color:C.body, lineHeight:1.7, maxWidth:520, margin:"0 auto 36px"}}>{t.hero_sub}</p>
        <div className="fu fu3" style={{display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginBottom:14}}>
          <button className="hov" onClick={startAudit} style={{background:C.blue, color:C.white, border:"none", borderRadius:10, padding:"14px 32px", fontWeight:800, fontSize:16, cursor:"pointer", fontFamily:"inherit", boxShadow:`0 4px 24px ${C.blue}40`}}>{t.hero_cta}</button>
        </div>
        <p className="fu fu3" style={{fontSize:12, color:C.muted}}>{t.hero_note}</p>
      </div>

      <div style={{maxWidth:860, margin:"64px auto 0", padding:"0 20px"}}>
        <p style={{textAlign:"center", fontSize:11, fontWeight:700, color:C.muted, letterSpacing:2, textTransform:"uppercase", marginBottom:28}}>{t.how_label}</p>
        <div className="steps-grid" style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:1, background:C.silverLt, borderRadius:14, overflow:"hidden"}}>
          {t.steps.map((s,i)=>(
            <div key={i} style={{background:C.white, padding:"28px 24px"}}>
              <div style={{fontSize:12, fontWeight:800, color:C.blue, letterSpacing:1, marginBottom:10}}>{s.num}</div>
              <div style={{fontSize:14, fontWeight:800, color:C.ink, marginBottom:8}}>{s.title}</div>
              <div style={{fontSize:13, color:C.body, lineHeight:1.6}}>{s.body}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-section" style={{maxWidth:560, margin:"64px auto 0", padding:"0 32px"}}>
        <div style={{background:C.white, border:`1px solid ${C.silverLt}`, borderRadius:16, overflow:"hidden", boxShadow:`0 8px 40px rgba(0,0,0,.07)`}}>
          <div style={{background:C.ink, padding:"28px 24px"}}>
            <div style={{fontSize:11, fontWeight:700, color:C.muted, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8}}>{t.pricing_label}</div>
            <div style={{display:"flex", alignItems:"flex-end", gap:8, marginBottom:4}}>
              <span style={{fontSize:52, fontWeight:900, color:C.white, lineHeight:1, letterSpacing:"-2px"}}>$49</span>
              <span style={{fontSize:14, color:C.muted, paddingBottom:8}}>{t.pricing_onetime}</span>
            </div>
            <div style={{fontSize:13, color:C.muted}}>{t.pricing_sub}</div>
          </div>
          <div style={{padding:"24px"}}>
            {t.features.map((f,i)=>(
              <div key={i} style={{display:"flex", alignItems:"flex-start", gap:10, marginBottom:12}}>
                <span style={{color:C.green, fontWeight:900, fontSize:14, flexShrink:0, marginTop:1}}>✓</span>
                <span style={{fontSize:13, color:C.body, lineHeight:1.5}}>{f}</span>
              </div>
            ))}
          </div>
          <div style={{padding:"0 24px 28px"}}>
            <button className="hov" onClick={startAudit} style={{width:"100%", background:C.blue, color:C.white, border:"none", borderRadius:10, padding:"14px", fontWeight:800, fontSize:15, cursor:"pointer", fontFamily:"inherit", boxShadow:`0 4px 20px ${C.blue}30`}}>{t.pricing_cta}</button>
            <p style={{textAlign:"center", fontSize:11, color:C.muted, marginTop:10}}>{t.pricing_note}</p>
          </div>
        </div>
      </div>

      <div style={{maxWidth:860, margin:"52px auto 0", padding:"0 20px", textAlign:"center"}}>
        <p style={{fontSize:11, fontWeight:700, color:C.muted, letterSpacing:2, textTransform:"uppercase", marginBottom:16}}>{t.platforms_label}</p>
        <div style={{display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center"}}>
          {["Google Business Profile","Facebook","Instagram","Yelp","Apple Maps","Yellow Pages","ZoomInfo","LinkedIn","BBB","TripAdvisor","OpenTable","Avvo","Healthgrades","Chamber of Commerce","Industry Directories"].map((p,i)=>(
            <span key={i} style={{background:C.offWhite, border:`1px solid ${C.silverLt}`, borderRadius:20, padding:"6px 14px", fontSize:12, color:C.body, fontWeight:500}}>{p}</span>
          ))}
        </div>
      </div>

      <div style={{textAlign:"center", padding:"52px 20px 40px", borderTop:`1px solid ${C.silverLt}`, marginTop:64}}>
        <div style={{fontSize:14, fontWeight:800, color:C.ink, marginBottom:4}}>PresenceAudit</div>
        <div style={{fontSize:12, color:C.muted}}>{t.footer_tagline}</div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // CHAT (FIX #6: go-back button)
  // ══════════════════════════════════════════════════════════════════════════
  if (phase==="chat") return (
    <div style={{height:"100vh", display:"flex", flexDirection:"column", background:C.bg, fontFamily:"inherit"}}>
      <style>{GS}</style>
      <div style={{background:C.white, borderBottom:`1px solid ${C.silverLt}`, height:56, display:"flex", alignItems:"center", padding:"0 16px", justifyContent:"space-between", flexShrink:0}}>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <img src="/Presence_Audit_Logo.png" alt="PresenceAudit" style={{width:100, height:100, objectFit:"contain"}}/>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <LangToggle/>
          <span style={{fontSize:11, color:C.muted, fontWeight:600}}>{t.chat_step} {qIndex+1}{t.chat_of}{Qs.length}</span>
          <div style={{width:88, height:4, background:C.silverLt, borderRadius:4, overflow:"hidden"}}>
            <div style={{height:"100%", width:`${((qIndex+1)/Qs.length)*100}%`, background:C.blue, transition:"width .4s ease", borderRadius:4}}/>
          </div>
        </div>
      </div>
      <div style={{flex:1, overflowY:"auto", padding:"20px 16px", display:"flex", flexDirection:"column", gap:12}}>
        {messages.map((m,i)=>(
          <div key={i} className="si" style={{display:"flex", justifyContent:m.role==="user"?"flex-end":"flex-start", alignItems:"flex-end", gap:8}}>
            {m.role==="bot"&&(<div style={{width:30, height:30, background:C.ink, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, flexShrink:0}}>🤖</div>)}
            <div className="chat-bubble" style={{maxWidth:"70%", background:m.role==="user"?C.ink:C.white, color:m.role==="user"?C.white:C.ink, padding:"11px 15px", borderRadius:m.role==="user"?"16px 16px 4px 16px":"4px 16px 16px 16px", fontSize:14, lineHeight:1.55, border:m.role==="bot"?`1px solid ${C.silverLt}`:"none", boxShadow:"0 1px 4px rgba(0,0,0,.05)", whiteSpace:"pre-wrap"}}>{m.text}</div>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>
      <div style={{background:C.white, borderTop:`1px solid ${C.silverLt}`, padding:"12px 16px", display:"flex", gap:8, flexShrink:0, alignItems:"center"}}>
        {qIndex > 0 && (
          <button className="hov" onClick={goBack}
            style={{background:C.offWhite, color:C.silver, border:`1px solid ${C.silverLt}`, borderRadius:10, padding:"11px 14px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap"}}>
            {t.chat_back}
          </button>
        )}
        <input ref={inputRef} value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();submitAnswer();}}}
          placeholder={Qs[qIndex]?.placeholder||""}
          style={{flex:1, padding:"11px 16px", borderRadius:10, border:`1.5px solid ${input.trim()?C.blue:C.ghost}`, fontSize:14, outline:"none", color:C.ink, background:C.white, transition:"border-color .2s", fontFamily:"inherit"}}
        />
        <button className="hov" onClick={submitAnswer} disabled={!input.trim()}
          style={{background:input.trim()?C.ink:C.silverLt, color:C.white, border:"none", borderRadius:10, padding:"11px 20px", fontWeight:800, fontSize:16, cursor:input.trim()?"pointer":"default", transition:"background .2s", fontFamily:"inherit"}}>→</button>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // LOADING (FIX #12: slow warning after 60s)
  // ══════════════════════════════════════════════════════════════════════════
  if (phase==="loading") return (
    <div style={{height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.white, fontFamily:"inherit"}}>
      <style>{GS}</style>
      <div style={{textAlign:"center", maxWidth:380, padding:"0 24px"}}>
        <div style={{width:56, height:56, borderRadius:"50%", border:`3px solid ${C.silverLt}`, borderTop:`3px solid ${C.blue}`, animation:"spin 1s linear infinite", margin:"0 auto 28px"}}/>
        <div style={{fontSize:20, fontWeight:800, color:C.ink, marginBottom:6, letterSpacing:"-.5px"}}>{t.loading_title} {answers.brand_name || answers.legal_name}</div>
        <div style={{fontSize:13, color:C.muted, marginBottom:28, minHeight:20}}>{loadingMsg}</div>
        {slowWarning && (
          <div style={{fontSize:12, color:C.amber, marginBottom:16, padding:"10px 16px", background:C.amberLt, border:`1px solid ${C.amberBorder}`, borderRadius:8}}>
            {t.loading_slow}
          </div>
        )}
        <div style={{background:C.silverLt, borderRadius:6, height:6, overflow:"hidden", marginBottom:10}}>
          <div style={{height:"100%", width:`${loadPct}%`, background:C.blue, borderRadius:6, transition:"width .7s ease"}}/>
        </div>
        <div style={{fontSize:12, color:C.muted, fontWeight:600}}>{loadPct}% complete</div>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ERROR
  // ══════════════════════════════════════════════════════════════════════════
  if (phase==="error") return (
    <div style={{height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.white, fontFamily:"inherit"}}>
      <style>{GS}</style>
      <div style={{background:C.white, border:`1px solid ${C.silverLt}`, borderRadius:16, padding:"44px 32px", textAlign:"center", maxWidth:380, margin:"0 20px", boxShadow:`0 4px 24px rgba(0,0,0,.06)`}}>
        <div style={{fontSize:38, marginBottom:16}}>⚠️</div>
        <div style={{fontSize:18, fontWeight:800, color:C.ink, marginBottom:8}}>{t.error_title}</div>
        <div style={{fontSize:13, color:C.body, lineHeight:1.6, marginBottom:24}}>{errorMsg}</div>
        <button className="hov" onClick={()=>{setPhase("landing");setErrorMsg("");}} style={{background:C.ink, color:C.white, border:"none", borderRadius:8, padding:"10px 24px", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"inherit"}}>{t.error_cta}</button>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // REPORT (FIX #2: mobile responsive grids, FIX #9: confirm reset)
  // ══════════════════════════════════════════════════════════════════════════
  const d         = auditData || {};
  const platforms = d.platforms || [];
  const critCount = platforms.filter(p=>p.status==="critical").length;
  const warnCount = platforms.filter(p=>p.status==="warning").length;
  const goodCount = platforms.filter(p=>p.status==="good").length;
  const teaserPlatforms = platforms.slice(0,3);
  const lockedPlatforms = platforms.slice(3);

  const PlatformCard = ({p, blurred=false}) => {
    const cfg=statusCfg(p.status);
    return (
      <div style={{background:C.white, borderRadius:12, padding:"14px 16px", border:`1px solid ${C.silverLt}`, borderLeft:`3px solid ${cfg.dot}`, boxShadow:`0 1px 4px rgba(0,0,0,.03)`}}>
        <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:5}}>
          <div style={{width:7, height:7, borderRadius:"50%", background:cfg.dot, flexShrink:0}}/>
          <span style={{fontWeight:800, fontSize:14, color:C.ink}}>{p.platform}</span>
          <span style={{background:cfg.badgeBg, color:cfg.badgeText, borderRadius:4, padding:"2px 9px", fontSize:9, fontWeight:800, letterSpacing:.5}}>{cfg.badge}</span>
          {!blurred && p.claimed && p.claimed!=="unknown"&&(
            <span style={{background:p.claimed==="yes"?C.greenLt:C.redLt, color:p.claimed==="yes"?C.green:C.red, border:`1px solid ${p.claimed==="yes"?C.greenBorder:C.redBorder}`, borderRadius:4, padding:"2px 8px", fontSize:9, fontWeight:700}}>
              {p.claimed==="yes"?t.claimed_yes:t.claimed_no}
            </span>
          )}
        </div>
        {!blurred && p.current_name&&p.correct_name&&p.current_name!==p.correct_name&&p.current_name!=="Not found"&&(
          <div style={{fontSize:11.5, marginBottom:5, overflowWrap:"break-word"}}>
            <span style={{color:C.red, fontWeight:700}}>"{p.current_name}"</span>
            <span style={{color:C.muted, margin:"0 6px"}}>{t.should_be}</span>
            <span style={{color:C.green, fontWeight:700}}>"{p.correct_name}"</span>
          </div>
        )}
        <div style={{fontSize:12.5, color:C.body, lineHeight:1.5, marginBottom:(!blurred&&p.action)?5:0}}>{p.issue}</div>
        {!blurred && p.action&&<div style={{fontSize:11.5, color:C.green, fontWeight:700, overflowWrap:"break-word"}}>→ {p.action}</div>}
      </div>
    );
  };

  return (
    <div id="audit-report" style={{background:C.bg, minHeight:"100vh", fontFamily:"inherit"}}>
      <style>{GS}</style>
      <div className="np" style={{position:"sticky", top:0, zIndex:20, background:C.white, borderBottom:`1px solid ${C.silverLt}`, height:52, display:"flex", alignItems:"center", padding:"0 16px", gap:8}}>
        <img src="/Presence_Audit_Logo.png" alt="PresenceAudit" style={{width:90, height:90, objectFit:"contain"}}/>
        <span style={{color:C.ghost, fontSize:13}}>—</span>
        <span style={{fontSize:13, color:C.body, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0, flex:"0 1 auto"}}>{d.business_name}</span>
        <div className="report-nav-btns" style={{marginLeft:"auto", display:"flex", gap:8, alignItems:"center", flexShrink:0}}>
          <LangToggle/>
          <button className="hov" onClick={reset} style={{background:C.offWhite, color:C.ink, border:`1px solid ${C.ghost}`, borderRadius:7, padding:"7px 14px", fontWeight:600, fontSize:12, cursor:"pointer", fontFamily:"inherit"}}>{t.report_new}</button>
          {unlocked&&<button className="hov" onClick={()=>window.print()} style={{background:C.ink, color:C.white, border:"none", borderRadius:7, padding:"7px 16px", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit"}}>{t.report_save}</button>}
        </div>
      </div>

      <div style={{maxWidth:860, margin:"0 auto", padding:"20px 16px 52px"}}>
        {/* Score card */}
        <div className="fu" style={{background:C.white, borderRadius:16, border:`1px solid ${C.silverLt}`, overflow:"hidden", marginBottom:12, boxShadow:`0 2px 16px rgba(0,0,0,.05)`}}>
          <div className="score-card-top" style={{padding:"24px 20px", display:"flex", gap:20, alignItems:"flex-start", flexWrap:"wrap", borderBottom:`1px solid ${C.silverLt}`}}>
            <div style={{textAlign:"center", minWidth:70}}>
              <div style={{fontSize:60, fontWeight:900, color:gradeColor(d.grade), lineHeight:1, letterSpacing:"-2px"}}>{d.grade}</div>
              <div style={{fontSize:11, color:C.muted, fontWeight:700, marginTop:2}}>{t.grade_labels[d.grade]||""}</div>
            </div>
            <div style={{flex:1, minWidth:180}}>
              <div style={{fontSize:20, fontWeight:900, color:C.ink, marginBottom:3, letterSpacing:"-.5px", overflowWrap:"break-word"}}>{d.business_name}</div>
              {d.legal_name&&d.legal_name!==d.business_name&&(<div style={{fontSize:11, color:C.muted, marginBottom:6}}>{t.report_legal} {d.legal_name}</div>)}
              <div style={{fontSize:12, color:C.muted, marginBottom:10}}>{answers.location} · {new Date().toLocaleDateString(lang==="es"?"es-US":"en-US",{month:"long",day:"numeric",year:"numeric"})}</div>
              <div style={{fontSize:13.5, color:C.body, lineHeight:1.6}}>{d.summary}</div>
            </div>
            <div className="report-stats" style={{display:"flex", gap:8, flexWrap:"wrap"}}>
              {[
                {label:t.print_labels[0],count:critCount,color:C.red,   bg:C.redLt,   border:C.redBorder  },
                {label:t.print_labels[1],count:warnCount,color:C.amber, bg:C.amberLt, border:C.amberBorder},
                {label:t.print_labels[2],count:goodCount,color:C.green, bg:C.greenLt, border:C.greenBorder},
              ].map(s=>(
                <div key={s.label} style={{background:s.bg, border:`1px solid ${s.border}`, borderRadius:10, padding:"10px 14px", textAlign:"center", minWidth:60, flex:"1"}}>
                  <div style={{fontSize:26, fontWeight:900, color:s.color, lineHeight:1}}>{s.count}</div>
                  <div style={{fontSize:9.5, color:s.color, fontWeight:700, marginTop:3, letterSpacing:.3}}>{s.label.toUpperCase()}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{padding:"14px 20px", display:"flex", alignItems:"center", gap:14}}>
            <span style={{fontSize:10.5, fontWeight:700, color:C.muted, letterSpacing:.8, whiteSpace:"nowrap"}}>{t.score_label}</span>
            <div style={{flex:1, height:6, background:C.silverLt, borderRadius:4, overflow:"hidden"}}>
              <div style={{height:"100%", width:`${d.score||0}%`, background:d.score>=75?C.green:d.score>=55?C.blue:d.score>=35?C.amber:C.red, borderRadius:4, transition:"width 1.2s ease"}}/>
            </div>
            <span style={{fontSize:14, fontWeight:900, color:C.ink}}>{d.score}<span style={{fontSize:11, color:C.muted, fontWeight:500}}>/100</span></span>
          </div>
        </div>

        {!d.nap_consistent&&(
          <div style={{background:C.redLt, border:`1px solid ${C.redBorder}`, borderLeft:`3px solid ${C.red}`, borderRadius:10, padding:"12px 16px", marginBottom:10, display:"flex", gap:10, alignItems:"flex-start"}}>
            <span style={{fontSize:15, flexShrink:0}}>🚨</span>
            <div>
              <div style={{fontSize:11, fontWeight:800, color:C.red, marginBottom:3}}>{t.nap_title}</div>
              <div style={{fontSize:12.5, color:C.body}}>{t.nap_body}</div>
            </div>
          </div>
        )}

        {(d.name_variations||[]).length>1&&(
          <div style={{background:C.white, borderRadius:12, padding:"14px 16px", marginBottom:10, border:`1px solid ${C.silverLt}`}}>
            <div style={{fontSize:10.5, fontWeight:700, color:C.muted, letterSpacing:.8, marginBottom:10}}>{t.name_var_label}</div>
            <div style={{display:"flex", flexWrap:"wrap", gap:7}}>
              {d.name_variations.map((v,i)=>(
                <span key={i} style={{background:i===0?C.greenLt:C.redLt, color:i===0?C.green:C.red, border:`1px solid ${i===0?C.greenBorder:C.redBorder}`, borderRadius:6, padding:"5px 12px", fontSize:12, fontWeight:700}}>
                  {i===0?"✓ ":"✗ "}{v}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{fontSize:10.5, fontWeight:700, color:C.muted, letterSpacing:.8, marginBottom:8, marginTop:4}}>{t.platform_label}</div>

        <div style={{display:"flex", flexDirection:"column", gap:7, marginBottom:7}}>
          {teaserPlatforms.map((p,i)=><PlatformCard key={i} p={p}/>)}
        </div>

        {!unlocked ? (
          <div style={{position:"relative", marginBottom:12}}>
            {lockedPlatforms.length>0&&(
              <div className="lock-blur" style={{display:"flex", flexDirection:"column", gap:7, marginBottom:7}}>
                {lockedPlatforms.map((p,i)=><PlatformCard key={i} p={p} blurred={true}/>)}
              </div>
            )}
            <div style={{background:C.white, borderRadius:16, border:`1.5px solid ${C.blue}`, overflow:"hidden", boxShadow:`0 8px 40px rgba(37,99,235,.12)`, position:"relative", zIndex:2}}>
              <div style={{background:`linear-gradient(135deg,${C.ink},#1E293B)`, padding:"28px 24px", textAlign:"center"}}>
                <div style={{fontSize:32, marginBottom:8}}>🔒</div>
                <div style={{fontSize:20, fontWeight:900, color:C.white, marginBottom:6, letterSpacing:"-.5px"}}>
                  {lockedPlatforms.length>0 ? `${lockedPlatforms.length} ${t.unlock_title_suffix}` : t.unlock_ready}
                </div>
                <div style={{fontSize:13, color:C.muted, lineHeight:1.6, maxWidth:380, margin:"0 auto"}}>{t.unlock_sub}</div>
              </div>
              <div style={{padding:"24px 20px"}}>
                <div className="unlock-features" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:20}}>
                  {t.unlock_features.map((f,i)=>(
                    <div key={i} style={{display:"flex", alignItems:"flex-start", gap:8}}>
                      <span style={{color:C.green, fontWeight:900, fontSize:13, flexShrink:0}}>✓</span>
                      <span style={{fontSize:12, color:C.body, lineHeight:1.4}}>{f}</span>
                    </div>
                  ))}
                </div>
                {payError&&(<div style={{background:C.redLt, border:`1px solid ${C.redBorder}`, borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:C.red}}>{payError}</div>)}
                <button className="hov" onClick={handlePayment} disabled={paying}
                  style={{width:"100%", background:paying?C.silver:C.blue, color:C.white, border:"none", borderRadius:10, padding:"16px", fontWeight:800, fontSize:16, cursor:paying?"default":"pointer", fontFamily:"inherit", boxShadow:`0 4px 20px ${C.blue}30`, transition:"background .2s"}}>
                  {paying ? t.unlock_processing : t.unlock_cta}
                </button>
                <p style={{textAlign:"center", fontSize:11, color:C.muted, marginTop:10}}>{t.unlock_secure}</p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{display:"flex", flexDirection:"column", gap:7, marginBottom:12}}>
              {lockedPlatforms.map((p,i)=><PlatformCard key={i} p={p}/>)}
            </div>
            <div className="issues-grid" style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10}}>
              {[
                {title:t.critical_title, titleColor:C.red,  items:d.critical_issues||[]},
                {title:t.quickwins_title,titleColor:C.green, items:d.quick_wins||[]},
              ].map(section=>(
                <div key={section.title} style={{background:C.white, borderRadius:12, padding:"16px", border:`1px solid ${C.silverLt}`}}>
                  <div style={{fontSize:10.5, fontWeight:800, color:section.titleColor, marginBottom:12, letterSpacing:.4}}>{section.title}</div>
                  {section.items.map((item,i)=>(
                    <div key={i} style={{display:"flex", gap:8, marginBottom:9, alignItems:"flex-start"}}>
                      <span style={{color:section.titleColor, fontWeight:900, fontSize:12, flexShrink:0}}>{i+1}.</span>
                      <span style={{fontSize:12.5, color:C.body, lineHeight:1.5}}>{item}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {(d.industry_directories_missing||[]).length>0&&(
              <div style={{background:C.amberLt, border:`1px solid ${C.amberBorder}`, borderLeft:`3px solid ${C.amber}`, borderRadius:10, padding:"13px 16px", marginBottom:10}}>
                <div style={{fontSize:10.5, fontWeight:800, color:C.amber, marginBottom:8}}>{t.industry_missing}</div>
                <div style={{display:"flex", flexWrap:"wrap", gap:7}}>
                  {d.industry_directories_missing.map((dir,i)=>(
                    <span key={i} style={{background:C.white, border:`1px solid ${C.amberBorder}`, borderRadius:6, padding:"4px 12px", fontSize:11.5, color:C.amber, fontWeight:700}}>+ {dir}</span>
                  ))}
                </div>
              </div>
            )}
            {(d.recommended_tagline_placements||[]).length>0&&(
              <div style={{background:C.blueLt, border:`1px solid ${C.blueMid}`, borderLeft:`3px solid ${C.blue}`, borderRadius:10, padding:"13px 16px", marginBottom:10}}>
                <div style={{fontSize:10.5, fontWeight:800, color:C.blue, marginBottom:8}}>
                  TAGLINE: {d.tagline_present?t.tagline_found:t.tagline_missing}
                </div>
                <div style={{display:"flex", flexWrap:"wrap", gap:7}}>
                  {d.recommended_tagline_placements.map((p,i)=>(
                    <span key={i} style={{background:C.white, border:`1px solid ${C.blueMid}`, borderRadius:6, padding:"4px 12px", fontSize:11.5, color:C.blue, fontWeight:700}}>+ {p}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="np" style={{background:C.white, borderRadius:14, padding:"28px 20px", textAlign:"center", border:`1px solid ${C.silverLt}`}}>
              <div style={{fontSize:19, fontWeight:800, color:C.ink, marginBottom:6, letterSpacing:"-.4px"}}>{t.footer_cta_title}</div>
              <div style={{fontSize:13, color:C.body, marginBottom:22}}>{t.footer_cta_sub}</div>
              <div style={{display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap"}}>
                <button className="hov" onClick={()=>window.print()} style={{background:C.ink, color:C.white, border:"none", borderRadius:10, padding:"12px 28px", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit"}}>{t.footer_pdf}</button>
                <button className="hov" onClick={reset} style={{background:C.white, color:C.ink, border:`1.5px solid ${C.ghost}`, borderRadius:10, padding:"12px 22px", fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit"}}>{t.footer_another}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Print cover */}
      <div className="po pb" style={{width:"100%", minHeight:"100vh", background:C.white, flexDirection:"column", position:"relative", overflow:"hidden", fontFamily:"inherit"}}>
        <div style={{position:"absolute", left:0, top:0, bottom:0, width:6, background:C.blue}}/>
        <div style={{paddingLeft:48, paddingRight:44, paddingTop:52, flex:1}}>
          <div style={{fontSize:10, fontWeight:700, letterSpacing:2, color:C.muted, textTransform:"uppercase", marginBottom:10}}>{t.print_label}</div>
          <div style={{fontSize:50, fontWeight:900, color:C.ink, lineHeight:1.05, marginBottom:6, letterSpacing:"-1.5px"}}>{d.business_name}</div>
          {d.legal_name&&d.legal_name!==d.business_name&&(<div style={{fontSize:13, color:C.muted, marginBottom:6}}>{t.report_legal} {d.legal_name}</div>)}
          <div style={{width:48, height:3, background:C.blue, marginBottom:16}}/>
          <div style={{fontSize:14, color:C.body, lineHeight:1.65, maxWidth:440}}>{t.print_sub}</div>
          <div style={{marginTop:40, display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 40px", maxWidth:460}}>
            {t.print_fields.map((k,i)=>{
              const vals=[answers.business_type,answers.location,answers.website,answers.phone,`${d.score}/100`,d.grade];
              return (
                <div key={k} style={{borderBottom:`1px solid ${C.silverLt}`, paddingBottom:7}}>
                  <div style={{fontSize:8, fontWeight:700, color:C.muted, letterSpacing:1, textTransform:"uppercase", marginBottom:2}}>{k}</div>
                  <div style={{fontSize:12, fontWeight:700, color:i===5?gradeColor(d.grade):C.ink}}>{vals[i]||"—"}</div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:32, background:C.blueLt, borderLeft:`3px solid ${C.blue}`, padding:"14px 18px", maxWidth:480}}>
            <div style={{fontSize:9, fontWeight:800, letterSpacing:1, color:C.blue, marginBottom:5, textTransform:"uppercase"}}>{t.print_finding}</div>
            <div style={{fontSize:12, color:C.body, lineHeight:1.6}}>{d.summary}</div>
          </div>
          <div style={{marginTop:32, display:"flex", gap:0, maxWidth:320, border:`1px solid ${C.silverLt}`, borderRadius:8, overflow:"hidden"}}>
            {[
              {label:t.print_labels[0],count:critCount,color:C.red,  bg:C.redLt  },
              {label:t.print_labels[1],count:warnCount,color:C.amber,bg:C.amberLt},
              {label:t.print_labels[2],count:goodCount,color:C.green, bg:C.greenLt},
              {label:t.print_labels[3],count:platforms.length,color:C.ink,bg:C.offWhite},
            ].map((s,i,a)=>(
              <div key={i} style={{flex:1, background:s.bg, padding:"11px 6px", textAlign:"center", borderRight:i<a.length-1?`1px solid ${C.silverLt}`:"none"}}>
                <div style={{fontSize:20, fontWeight:900, color:s.color, lineHeight:1}}>{s.count}</div>
                <div style={{fontSize:8, fontWeight:700, color:s.color, letterSpacing:.4, textTransform:"uppercase", marginTop:3}}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{background:C.ink, padding:"16px 48px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <div style={{fontSize:11, fontWeight:800, color:C.white}}>PresenceAudit</div>
          <div style={{fontSize:9, color:C.muted}}>{new Date().toLocaleDateString()}</div>
        </div>
      </div>
    </div>
  );
}
